import React, { memo } from "react"
import { Div } from "atomize"
import NewLessonModal from "./modals/NewLessonModal"
import NewMatchModal from "./modals/NewMatchModal"
import EditLessonModal from "./modals/EditLessonModal"
import DeleteLessonModal from "./modals/DeleteLessonModal"
import LessonContentModal from "./modals/LessonContentModal"
import LessonDiaryModal from "./modals/LessonDiaryModal"
import NotebookEntryModal from "./modals/NotebookEntryModal"
import RemoveNotebookEntryModal from "./modals/RemoveNotebookEntryModal"
import RestoreLessonModal from "./modals/RestoreLessonModal"
import RescheduleLessonModal from "./modals/RescheduleLessonModal"
import ReinsertLessonModal from "./modals/ReinsertLessonModal"
import { isCancelledLesson } from "./utils/cancellation.utils"
import { motion, useDragControls } from "framer-motion"
import Modal from "./atomize/Modal"
import AuthModal from "./modals/AuthModal"
import DuplicateLessonModal from "./modals/DuplicateLessonModal"
import ExplanationModal from "./modals/ExplanationModal"
import NewPlayerModal from "./modals/NewPlayerModal"
import EditPlayerModal from "./modals/EditPlayerModal"
import ArchivePlayerModal from "./modals/ArchivePlayerModal"
import PlayerLessonsModal from "./modals/PlayerLessonsModal"
import TechnicalSheetModal from "./modals/TechnicalSheetModal"
import GoogleCalendarModal from "./modals/GoogleCalendarModal"
import SchoolSectionModal from "./modals/SchoolSectionModal"
import LessonTypeLessonsModal from "./modals/LessonTypeLessonsModal"
import RegistrationModal from "./modals/RegistrationModal"
import ImportPlayersModal from "./modals/ImportPlayersModal"
import DeleteAccountModal from "./modals/DeleteAccountModal"
import PictureModal from "./modals/PictureModal"
import SchoolInviteModal from "./modals/SchoolInviteModal"
import ProposeAppModal from "./modals/ProposeAppModal"
import JoinSchoolModal from "./modals/JoinSchoolModal"
import LeaveSchoolModal from "./modals/LeaveSchoolModal"
import MySchoolsModal from "./modals/MySchoolsModal"
import DeleteSchoolModal from "./modals/DeleteSchoolModal"
import SchoolTeamModal from "./modals/SchoolTeamModal"
import LessonDeletionsModal from "./modals/LessonDeletionsModal"
import RevokeColleagueModal from "./modals/RevokeColleagueModal"
import AddSubstituteModal from "./modals/AddSubstituteModal"
import PlayerAbsencesModal from "./modals/PlayerAbsencesModal"
import MissingPaymentMethodModal from "./modals/MissingPaymentMethodModal"
import FeedbackModal from "./modals/FeedbackModal"
import GuideModal from "./modals/GuideModal"
import HelpTipModal from "./modals/HelpTipModal"
import DevVersionsModal from "./modals/DevVersionsModal"
import { theme } from "./settings/config"
import { useBackClose } from "./atoms/useBackClose"

// Il tasto indietro del telefono chiude il foglio invece di cambiare pagina
// (src/atoms/useBackClose.js). Sta in un componente suo perché la voce nella
// cronologia va messa quando il foglio si apre e tolta quando si chiude.
const BackClose = ({ onBack }) => {
	useBackClose(onBack)
	return null
}

const SModalContainer = ({ children, isOpen, onClose }) => {
	// Il modal si trascina solo dalla maniglia in cima: altrimenti scorrere
	// un elenco dentro il modal lo farebbe scivolare verso il basso.
	const dragControls = useDragControls()

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			bg="transparent"
			pos="absolute"
			bottom="-5px"
			maxW="none"
			p="0"
			m="0">
			{/* Prima del contenuto: se dentro il foglio si apre subito qualcosa
			    con la sua voce, la sua va sopra quella del foglio. La
			    registrazione da finire non si chiude, e non ne ha: indietro lì
			    torna al passo di prima (RegistrationModal). */}
			{isOpen && onClose && <BackClose onBack={onClose} />}
			{isOpen && (
				<motion.div
					initial={{ y: 200 }}
					animate={{ y: 0 }}
					transition={{
						type: "tween",
						ease: [0.04, 0.62, 0.23, 0.98],
						duration: 0.4,
					}}
					drag="y"
					dragControls={dragControls}
					dragListener={false}
					dragConstraints={{ top: 0 }}
					dragElastic={{ top: 0, bottom: 0.3 }}
					onDragEnd={(_, info) => {
						if (info.offset.y > 100 || info.velocity.y > 500) {
							onClose?.()
						}
					}}>
					<Div
						bg="background"
						rounded="t-20px"
						p={{
							b: "calc(1.25rem + 2*env(safe-area-inset-bottom))",
							x: "0.5rem",
						}}>
						<Div
							p={{ y: "0.75rem" }}
							cursor="grab"
							style={{ touchAction: "none" }}
							onPointerDown={(e) => dragControls.start(e)}>
							<Div
								w="2.5rem"
								h="4px"
								bg={theme.colors.surface}
								rounded="circle"
								m={{ x: "auto" }}
							/>
						</Div>
						{children}
					</Div>
				</motion.div>
			)}
		</Modal>
	)
}

function SModal({ isOpen, onClose, type, data, action, step }) {
	var modalContent

	switch (type) {
		case "auth":
			modalContent = <AuthModal closeModal={onClose} initialMode={data} />
			break
		case "newLesson":
			modalContent = <NewLessonModal closeModal={onClose} selectedDate={data} />
			break
		// tenendo premuto il "+": quando giochi tu
		case "newMatch":
			modalContent = <NewMatchModal closeModal={onClose} selectedDate={data} />
			break
		case "editLesson":
			// Una lezione annullata non si modifica: la si ripristina. Ci si
			// arriva da tanti posti — il profilo, la scheda di un allievo, la
			// settimana — e qui li si prende tutti.
			modalContent = isCancelledLesson(data) ? (
				<RestoreLessonModal closeModal={onClose} lessonData={data} />
			) : (
				<EditLessonModal closeModal={onClose} lessonData={data} />
			)
			break
		case "restoreLesson":
			modalContent = <RestoreLessonModal closeModal={onClose} lessonData={data} />
			break
		case "rescheduleLesson":
			modalContent = <RescheduleLessonModal closeModal={onClose} lessonData={data} />
			break
		case "reinsertLesson":
			modalContent = <ReinsertLessonModal closeModal={onClose} lessonData={data} />
			break
		case "duplicateLesson":
			modalContent = <DuplicateLessonModal closeModal={onClose} lessonData={data} />
			break
		// il contenuto tecnico, dalla lezione aperta
		case "lessonContent":
			modalContent = <LessonContentModal closeModal={onClose} lessonData={data} />
			break
		// com'è andata, a fine lezione
		case "lessonDiary":
			modalContent = <LessonDiaryModal closeModal={onClose} lessonData={data} />
			break
		case "deleteLesson":
			modalContent = <DeleteLessonModal closeModal={onClose} lessonData={data} />
			break
		case "explanation":
			modalContent = <ExplanationModal closeModal={onClose} subject={data} />
			break
		case "newPlayer":
			modalContent = <NewPlayerModal closeModal={onClose} />
			break
		case "editPlayer":
			modalContent = <EditPlayerModal closeModal={onClose} playerData={data} />
			break
		case "archivePlayer":
			modalContent = <ArchivePlayerModal closeModal={onClose} playerData={data} />
			break
		// una voce del quaderno: `entryID` per aprirne una (e `variantID` per
		// la variante da cambiare, o da cui partire con `asNewVariant`), `kind`
		// per una nuova
		case "notebookEntry":
			modalContent = (
				<NotebookEntryModal
					closeModal={onClose}
					entryID={data?.entryID}
					variantID={data?.variantID}
					asNewVariant={data?.asNewVariant}
					kind={data?.kind}
				/>
			)
			break
		// "Togli dal quaderno" dalla voce aperta di un esercizio: la conferma
		case "removeNotebookEntry":
			modalContent = <RemoveNotebookEntryModal closeModal={onClose} entryID={data?.entryID} />
			break
		// la scheda tecnica, dall'allievo aperto
		case "technicalSheet":
			modalContent = <TechnicalSheetModal closeModal={onClose} playerData={data} />
			break
		case "playerLessons":
			modalContent = <PlayerLessonsModal closeModal={onClose} playerData={data} />
			break
		case "deleteAccount":
			modalContent = <DeleteAccountModal closeModal={onClose} />
			break
		case "schoolTeam":
			modalContent = <SchoolTeamModal closeModal={onClose} />
			break
		case "lessonDeletions":
			modalContent = <LessonDeletionsModal closeModal={onClose} />
			break
		case "schoolInvite":
			modalContent = <SchoolInviteModal closeModal={onClose} />
			break
		// dai Primi passi: il codice per la direzione, dentro un messaggio
		case "proposeApp":
			modalContent = <ProposeAppModal closeModal={onClose} />
			break
		case "joinSchool":
			modalContent = <JoinSchoolModal closeModal={onClose} />
			break
		case "leaveSchool":
			modalContent = <LeaveSchoolModal closeModal={onClose} />
			break
		case "mySchools":
			modalContent = <MySchoolsModal closeModal={onClose} />
			break
		case "deleteSchool":
			modalContent = <DeleteSchoolModal closeModal={onClose} />
			break
		case "revokeColleague":
			modalContent = <RevokeColleagueModal closeModal={onClose} colleague={data} />
			break
		case "importPlayers":
			modalContent = <ImportPlayersModal closeModal={onClose} />
			break
		case "register":
			modalContent = <RegistrationModal />
			break
		case "newSchool":
			modalContent = <RegistrationModal onlySchool closeModal={onClose} />
			break
		case "schoolSection":
			modalContent = <SchoolSectionModal closeModal={onClose} section={data} />
			break
		// la foto del maestro o il logo del circolo: { kind, back }
		case "picture":
			modalContent = <PictureModal closeModal={onClose} kind={data?.kind} back={data?.back} />
			break
		case "addSubstitute":
			modalContent = (
				<AddSubstituteModal
					closeModal={onClose}
					lessonData={data.lessonData}
					absent={data.absent}
				/>
			)
			break
		case "playerAbsences":
			modalContent = <PlayerAbsencesModal closeModal={onClose} selection={data} />
			break
		case "missingPaymentMethod":
			modalContent = <MissingPaymentMethodModal closeModal={onClose} selection={data} />
			break
		case "lessonTypeLessons":
			modalContent = <LessonTypeLessonsModal closeModal={onClose} selection={data} />
			break
		case "feedback":
			modalContent = <FeedbackModal closeModal={onClose} />
			break
		// la guida, dal profilo o dal rimando di un "?" (src/help/)
		case "guide":
			modalContent = <GuideModal closeModal={onClose} topicID={data?.topicID} />
			break
		case "helpTip":
			modalContent = <HelpTipModal closeModal={onClose} tip={data} />
			break
		case "devVersions":
			modalContent = <DevVersionsModal closeModal={onClose} />
			break
		case "googleCalendar":
			modalContent = <GoogleCalendarModal closeModal={onClose} />
			break
		default:
			break
	}

	return (
		<SModalContainer
			isOpen={isOpen}
			onClose={type === "register" ? null : onClose}
			type={type}>
			{modalContent}
		</SModalContainer>
	)
}

export default memo(SModal)
